import type { SubscriberResponse, SubscriberData } from '@cvo/domain/api';
import type { User } from '@cvo/domain/models/User';

export const useUserStore = defineStore('user', () => {
  const runtimeConfig = useRuntimeConfig();
  const { locale } = useI18n();
  const defaultCurrency = runtimeConfig.public.defaultCurrency as string;
  const selectedCurrency = ref<string>(defaultCurrency);

  // State
  const subscriber = ref<SubscriberResponse | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Composables
  const api = useApi();
  const { getUser, isAuthenticated, getUserFromToken } = useAuth();

  // Async request handlers
  const updateSubscriberRequest = useAsyncRequest(performUpdate);

  // Getters
  const user = computed<User | null>(() => {
    if (subscriber.value?.person) {
      return subscriber.value.person;
    }

    const tokenUser = getUserFromToken();
    if (tokenUser) {
      return {
        id: tokenUser.sub || '',
        firstName: tokenUser.givenName || '',
        lastName: tokenUser.familyName || '',
        email: tokenUser.email,
        locale: tokenUser.locale || locale.value,
        dateOfBirth: '',
        currency: selectedCurrency.value,
      } satisfies User;
    }

    return null;
  });

  const subscriberId = computed(() => subscriber.value?.id);
  const subscriptionIds = computed<string[]>(() => subscriber.value?.subscriptionIds ?? []);
  const currency = computed<string>(() => subscriber.value?.person.currency ?? selectedCurrency.value);
  const displayName = computed(() => {
    if (!user.value) return '';
    return `${user.value.firstName || ''} ${user.value.lastName || ''}`.trim();
  });

  const isActiveUser = computed(() => {
    return isAuthenticated.value && subscriptionIds.value.length > 0;
  });

  const isInactiveUser = computed(() => {
    return isAuthenticated.value && subscriptionIds.value.length === 0;
  });

  // Actions
  async function fetchUser(): Promise<SubscriberResponse | null> {
    if (!isAuthenticated.value) {
      subscriber.value = null;
      return null;
    }

    isLoading.value = true;
    error.value = null;

    try {
      subscriber.value = await getUser();
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load user profile';
      console.error('Error loading user profile:', err);
      useO11y().reportError(err, { context: 'fetch_user' });
    } finally {
      isLoading.value = false;
    }

    return subscriber.value;
  };

  function clearUser(): void {
    subscriber.value = null;
    isLoading.value = false;
    error.value = null;
  };

  async function performUpdate(subscriberData: SubscriberData): Promise<void> {
    try {
      const updatedSubscriber = await api.subscriber.updateUserInfo(subscriberData.person, subscriberData.loyaltyPrograms).then(res => res.data);
      subscriber.value = updatedSubscriber;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update user profile';
      throw err;
    }
  }

  return {
    // State
    subscriber,
    selectedCurrency,
    isLoading: readonly(isLoading),
    error: readonly(error),

    // Getters
    user,
    subscriberId,
    subscriptionIds,
    isAuthenticated,
    isActiveUser,
    isInactiveUser,
    displayName,
    currency,

    // Actions
    fetchUser,
    clearUser,
    updateSubscriber: updateSubscriberRequest.run,

    // Update request states
    isUpdating: updateSubscriberRequest.isLoading,
    updateError: updateSubscriberRequest.error,
    updateSuccess: updateSubscriberRequest.success,
  };
}, {
  persist: {
    pick: ['selectedCurrency'],
  },
});
